import { useState, useEffect, useCallback } from 'react'

export interface OfferCondition {
  id: string
  title: string
  content: string
  isDefault: boolean // Se marcada, vem pré-selecionada em novas ofertas
  createdAt: string
  updatedAt: string
}

const STORAGE_KEY = 'offer_conditions'

/**
 * Hook para gerenciar condições de ofertas
 * Por enquanto usa localStorage, futuramente será migrado para API
 */
export function useOfferConditions() {
  const [conditions, setConditions] = useState<OfferCondition[]>([])
  const [loading, setLoading] = useState(true)

  // Load conditions on mount
  useEffect(() => {
    loadConditions()
  }, [])

  const loadConditions = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as OfferCondition[]
        setConditions(parsed)
      }
    } catch (error) {
      console.error('Error loading conditions:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // Save a new condition
  const saveCondition = useCallback((
    title: string,
    content: string,
    isDefault: boolean = false
  ): OfferCondition => {
    const newCondition: OfferCondition = {
      id: `condition-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title,
      content,
      isDefault,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const all = stored ? JSON.parse(stored) as OfferCondition[] : []
      all.push(newCondition)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
      loadConditions()
    } catch (error) {
      console.error('Error saving condition:', error)
    }

    return newCondition
  }, [loadConditions])

  // Update a condition
  const updateCondition = useCallback((
    id: string,
    title: string,
    content: string,
    isDefault?: boolean
  ) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const all = JSON.parse(stored) as OfferCondition[]
        const index = all.findIndex(c => c.id === id)
        if (index !== -1) {
          all[index] = {
            ...all[index],
            title,
            content,
            isDefault: isDefault !== undefined ? isDefault : all[index].isDefault,
            updatedAt: new Date().toISOString(),
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
          loadConditions()
        }
      }
    } catch (error) {
      console.error('Error updating condition:', error)
    }
  }, [loadConditions])

  // Delete a condition
  const deleteCondition = useCallback((id: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const all = JSON.parse(stored) as OfferCondition[]
        const filtered = all.filter(c => c.id !== id)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
        loadConditions()
      }
    } catch (error) {
      console.error('Error deleting condition:', error)
    }
  }, [loadConditions])

  // Toggle default status
  const toggleDefault = useCallback((id: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const all = JSON.parse(stored) as OfferCondition[]
        const index = all.findIndex(c => c.id === id)
        if (index !== -1) {
          all[index] = {
            ...all[index],
            isDefault: !all[index].isDefault,
            updatedAt: new Date().toISOString(),
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
          loadConditions()
        }
      }
    } catch (error) {
      console.error('Error toggling default:', error)
    }
  }, [loadConditions])

  // Get default conditions (pre-selected for new offers)
  const getDefaultConditions = useCallback((): OfferCondition[] => {
    return conditions.filter(c => c.isDefault)
  }, [conditions])

  return {
    conditions,
    loading,
    saveCondition,
    updateCondition,
    deleteCondition,
    toggleDefault,
    getDefaultConditions,
    refresh: loadConditions,
  }
}
